import { Box, Typography, createFilterOptions, FilterOptionsState } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useInsuranceCompaniesQuery } from '@uplift/queries';
import { useMemo } from 'react';
import { useL10n } from '@uplift/hooks';
import { Autocomplete } from '../Inputs';
import { cashPay } from '../Constants';
import { InsuranceCompany, InsuranceMetaGrouping } from '@uplift/api';
import groupBy from 'lodash/groupBy';


export type InsuranceAutocompleteType = {
  name: string;
  label?: string;
  className?: string;
  filterInsuranceCompanies?: (insuranceCompanies: InsuranceCompany[]) => InsuranceCompany[];
  excludeCashPay?: boolean;
  helperText?: React.ReactNode;
};

type InsuranceSelectItem = {
  id: string;
  disabled?: boolean;
  name: string;
  metaGrouping?: InsuranceMetaGrouping | null;
};
const filter = createFilterOptions<InsuranceSelectItem>();

const newCashPay = {
  ...cashPay,
  name: "None—l'll pay for services myself",
  metaGrouping: undefined,
};

const getGroupKey = (option: { metaGrouping?: string }) => {
  if (option.metaGrouping === 'medicare') {
    return 'Medicare';
  }

  if (option.metaGrouping === 'medicaid') {
    return 'Medicaid';
  }

  if (option.metaGrouping === undefined) {
    return '';
  }

  return 'Uplift insurance partners';
};

const NO_INSURANCE_ID = 'noInsuranceItem';

export const InsuranceAutocomplete: React.VFC<InsuranceAutocompleteType> = ({
  name,
  label,
  className,
  filterInsuranceCompanies,
  excludeCashPay = false,
  helperText,
}) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const { data: insuranceCompanies = [], isLoading: loading } = useInsuranceCompaniesQuery({ onlyAvailable: true });

  const insurances: InsuranceSelectItem[] = useMemo(() => {
    const filteredInsuranceCompanies = filterInsuranceCompanies
      ? filterInsuranceCompanies(insuranceCompanies)
      : insuranceCompanies;

    const insurances = filteredInsuranceCompanies.map(insurance => ({
      id: insurance.id,
      name: insurance.name,
      disabled: false,
      metaGrouping: insurance.metaGrouping
    }));

    const groupedInsurances = groupBy(insurances, insurance => insurance.metaGrouping ?? 'partner');

    Object.keys(groupedInsurances).forEach(key => {
      groupedInsurances[key] = groupedInsurances[key].sort((a, b) => {
        if (key === InsuranceMetaGrouping.medicare) {
          if (a.name === 'Medicare (Original)') return -1;
          if (b.name === 'Medicare (Original)') return 1;
        }
        return a.name.localeCompare(b.name);
      });
    });

    const orderedInsurances = [
      ...(groupedInsurances['partner'] || []),
      ...(groupedInsurances[InsuranceMetaGrouping.medicare] || []),
      ...(groupedInsurances[InsuranceMetaGrouping.medicaid] || []),
    ];

    if (excludeCashPay) {
      return orderedInsurances;
    }

    return [
      {
        id: newCashPay.id,
        name: newCashPay.name,
        disabled: false,
      },
      ...orderedInsurances
    ];
  }, [ filterInsuranceCompanies, insuranceCompanies, excludeCashPay ]);

  const renderOption = (option: InsuranceSelectItem) => {
    if (option.id === NO_INSURANCE_ID) {
      return (
        <Box>
          <Typography variant="body1" color="textPrimary">{l10n.insurance.dontSeeInsurance}</Typography>
          <Typography variant="body2">
            {excludeCashPay
              ? l10n.insurance.moreCarriersComingSoonExcludeCashPay
              : l10n.insurance.moreCarriersComingSoon
            }
          </Typography>
        </Box>
      );
    }
    if (option === newCashPay) {
      return (
        <span className={classes.fallbackOption}>{option.name}</span>
      );
    }

    return option.name;
  };

  const filterOptions = (options: InsuranceSelectItem[], state: FilterOptionsState<InsuranceSelectItem>) => {
    const items = filter(options, state);

    if (!items.length) {
      items.push({
        id: NO_INSURANCE_ID,
        disabled: true,
        name: '',
      });
      if (!excludeCashPay) {
        items.push(newCashPay);
      }
    }

    return items;
  };

  return (
    <Autocomplete
      className={className}
      classes={{
        option: classes.option,
        paper: classes.paper,
        popper: classes.popper
      }}
      fullWidth
      loading={loading}
      name={name}
      label={label}
      options={insurances}
      groupBy={getGroupKey}
      transformValue={v => v?.id}
      placeholder="Select insurance"
      getOptionLabel={opt => opt?.name ?? ''}
      renderOption={renderOption}
      getOptionDisabled={o => !o?.id}
      filterOptions={filterOptions}
      helperText={helperText}
      InputProps={{
        className: classes.overflowPadding
      }}
    />
  );
};

const useStyles = makeStyles({ name: 'InsuranceAutocomplete' })(theme => ({
  option: {
    '&[aria-disabled="true"]': {
      opacity: 1,
    }
  },
  fallbackOption: {
    fontWeight: 500,
  },
  paper: {
    maxHeight: '300px',
    overflow: 'auto',
  },
  popper: {
    '& ul': {
      maxHeight: 'unset',
      overflow: 'unset'
    }
  },
  overflowPadding: {
    paddingRight: theme.spacing(8)
  }
}));
